// src/pages/TroubleshootingRequest.jsx
// Troubleshooting Request — Creates a SIM-T ticket

import React, { useState } from 'react';

const tenantOptions = [
    'Tenant 178', 'Tenant 290', 'Tenant 310', 'Tenant 321', 'Tenant 333', 'Tenant 398', 'Other'
];

const TroubleshootingRequest = ({ userAlias }) => {
    const [tenant, setTenant] = useState('');
    const [affectedUsers, setAffectedUsers] = useState('');
    const [permissionGroup, setPermissionGroup] = useState('');
    const [issueDescription, setIssueDescription] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [whenStarted, setWhenStarted] = useState('');
    const [stepsToReproduce, setStepsToReproduce] = useState('');
    const [existingRef, setExistingRef] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [submitError, setSubmitError] = useState('');

    const isValid = tenant && issueDescription.trim() && affectedUsers.trim();

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const { getAccessToken } = await import('../services/auth');
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
            const token = getAccessToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const descBody = [
                `## Troubleshooting Request`,
                ``,
                `**Requester:** ${userAlias}`,
                `**Tenant:** ${tenant}`,
                ``,
                `---`,
                `**1. Affected user(s):** ${affectedUsers || 'N/A'}`,
                ``,
                `**2. Permission/LDAP group:** ${permissionGroup || 'N/A'}`,
                ``,
                `**3. Describe the issue:** ${issueDescription || 'N/A'}`,
                ``,
                `**4. Error message (if any):** ${errorMessage || 'N/A'}`,
                ``,
                `**5. When did it start:** ${whenStarted || 'N/A'}`,
                ``,
                `**6. Steps to reproduce:** ${stepsToReproduce || 'N/A'}`,
                ``,
                `**7. Existing SIM/reference:** ${existingRef || 'None'}`,
            ].join('\n');

            const response = await fetch(`${API_BASE_URL}/validate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    operation: 'createTicket',
                    title: `[Troubleshooting] ${tenant} - ${affectedUsers.split(',')[0].trim()}`,
                    description: descBody,
                    severity: 'SEV_5',
                    requesterAlias: userAlias
                })
            });

            const raw = await response.json();
            const result = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;

            if (response.status === 401) {
                sessionStorage.clear();
                window.location.href = '/';
                return;
            }

            if (!response.ok || result.error) {
                throw new Error(result.error || `API error: ${response.status}`);
            }

            setSubmitSuccess(result);
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitSuccess) {
        return (
            <div style={{ maxWidth: '700px', margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h2 style={{ color: '#166534', marginBottom: '8px' }}>Troubleshooting Request Submitted!</h2>
                <p style={{ fontSize: '14px', color: '#555', marginBottom: '8px' }}>Ticket ID: <strong>{submitSuccess.ticketId}</strong></p>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>📎 You can attach screenshots directly on the SIM ticket.</p>
                {submitSuccess.ticketUrl && (
                    <a href={submitSuccess.ticketUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-block', padding: '10px 20px', borderRadius: '8px', background: '#0073bb', color: '#fff', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                        View Ticket on SIM-T ↗
                    </a>
                )}
                <div style={{ marginTop: '20px' }}>
                    <button onClick={() => window.location.href = '/'} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
                        ← Back to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 24px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '4px' }}>🔧 Troubleshooting</h1>

            <div style={{ padding: '16px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', marginBottom: '16px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>
                <p style={{ margin: '0' }}>Use this form to report issues with permissions, access, or security tools managed by P&S. Please provide as much detail as possible to help us resolve your issue quickly.</p>
            </div>

            <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '20px', fontSize: '12px', color: '#92400e' }}>
                📎 <strong>Note:</strong> After submission, you can attach screenshots directly on the SIM ticket.
            </div>

            {submitError && <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '13px', color: '#991b1b', marginBottom: '16px' }}>❌ {submitError}</div>}

            {/* Tenant */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Tenant <span style={{ color: '#d13212' }}>*</span></label>
                <select value={tenant} onChange={(e) => setTenant(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}>
                    <option value="">— Select —</option>
                    {tenantOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            {/* Affected Users */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Affected user(s) <span style={{ color: '#d13212' }}>*</span></label>
                <input type="text" value={affectedUsers} onChange={(e) => setAffectedUsers(e.target.value)} placeholder="user1, user2 (comma-separated, without @amazon)"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* Permission/LDAP group */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Permission/LDAP group:</label>
                <input type="text" value={permissionGroup} onChange={(e) => setPermissionGroup(e.target.value)} placeholder="e.g., Paragon Investigations - Base PI"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* Issue Description */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Describe the issue <span style={{ color: '#d13212' }}>*</span></label>
                <textarea value={issueDescription} onChange={(e) => setIssueDescription(e.target.value)} rows={4} placeholder="What is happening? What did you expect to happen?"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Error Message */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Error message (if any):</label>
                <textarea value={errorMessage} onChange={(e) => setErrorMessage(e.target.value)} rows={2} placeholder="Copy-paste the error message here..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* When did it start */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>When did it start:</label>
                <input type="text" value={whenStarted} onChange={(e) => setWhenStarted(e.target.value)} placeholder="e.g., Aug 10, 2026 or 2 days ago"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* Steps to reproduce */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Steps to reproduce:</label>
                <textarea value={stepsToReproduce} onChange={(e) => setStepsToReproduce(e.target.value)} rows={3} placeholder="1. Go to... 2. Click on... 3. See error..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Existing reference */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Existing SIM/reference:</label>
                <input type="text" value={existingRef} onChange={(e) => setExistingRef(e.target.value)} placeholder="Link to existing SIM or reference..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* Submit */}
            <button onClick={handleSubmit} disabled={!isValid || isSubmitting}
                style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: isValid && !isSubmitting ? 'pointer' : 'not-allowed', border: 'none', background: isValid ? '#dc2626' : '#ccc', color: '#fff' }}>
                {isSubmitting ? '⏳ Submitting...' : '🚀 Submit Troubleshooting Request'}
            </button>
        </div>
    );
};

export default TroubleshootingRequest;
